/* ============================================================
   Google Reviews — Web Components
   google-reviews.js
   ============================================================ */

/**
 * <google-stars rating="4.5" size="18">
 * Renders star icons based on a rating (0–5), supports halves.
 */
class GoogleStars extends HTMLElement {
    connectedCallback() {
        this.render();
    }

    static get observedAttributes() {
        return ['rating', 'size'];
    }

    attributeChangedCallback() {
        this.render();
    }

    render() {
        const rating = parseFloat(this.getAttribute('rating') || '5');
        const size = parseInt(this.getAttribute('size') || '18', 10);
        const color = '#f4b400';
        const emptyColor = '#dadce0';

        let html = '';

        for (let i = 1; i <= 5; i++) {
            if (rating >= i) {
                // Full star
                html += `<svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" viewBox="0 0 24 24" aria-hidden="true">
          <path fill="${color}" d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z"/>
        </svg>`;
            } else if (rating >= i - 0.5) {
                // Half star
                html += `<svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" viewBox="0 0 24 24" aria-hidden="true">
          <defs>
            <linearGradient id="half-${i}-${size}">
              <stop offset="50%" stop-color="${color}"/>
              <stop offset="50%" stop-color="${emptyColor}"/>
            </linearGradient>
          </defs>
          <path fill="url(#half-${i}-${size})" d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z"/>
        </svg>`;
            } else {
                // Empty star
                html += `<svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" viewBox="0 0 24 24" aria-hidden="true">
          <path fill="${emptyColor}" d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z"/>
        </svg>`;
            }
        }

        this.innerHTML = html;
        this.setAttribute('role', 'img');
        this.setAttribute('aria-label', `${rating} out of 5 stars`);
    }
}

customElements.define('google-stars', GoogleStars);


/**
 * <google-reviews-slider data-autoplay="false" data-autoplay-speed="4000">
 * Handles the reviews carousel: drag, nav buttons, dots, autoplay.
 */
class GoogleReviewsSlider extends HTMLElement {
    constructor() {
        super();
        this._currentIndex = 0;
        this._visibleCount = 4;
        this._cards = [];
        this._dots = [];
        this._autoplayTimer = null;
        this._touchStartX = 0;
        this._touchEndX = 0;
        this._isDragging = false;
        this._dragStartX = 0;
        this._dragOffset = 0;
    }

    connectedCallback() {
        this._track = this.querySelector('.gr-slider-track');
        this._container = this.querySelector('.gr-slider-track-container');
        this._prevBtn = this.querySelector('.gr-nav-btn--prev');
        this._nextBtn = this.querySelector('.gr-nav-btn--next');
        this._dotsContainer = this.querySelector('.gr-dots');
        this._cards = Array.from(this._track.querySelectorAll('.gr-review-card'));

        if (!this._cards.length) return;

        this._setupShowMoreButtons();
        this._setupResizeObserver();
        this._updateVisibleCount();
        this._buildDots();
        this._updateSlider(false);
        this._attachNavListeners();
        this._attachTouchListeners();
        this._attachDragListeners();
        this._startAutoplay();

        // Stagger card animations on load
        this._cards.forEach((card, i) => {
            card.style.animationDelay = `${i * 0.07}s`;
        });
    }

    disconnectedCallback() {
        this._stopAutoplay();
        if (this._resizeObserver) this._resizeObserver.disconnect();
    }

    // ---- Show more / Show less ----
    _setupShowMoreButtons() {
        this._track.querySelectorAll('.gr-show-more-btn').forEach(btn => {
            btn.addEventListener('click', () => {
                const p = btn.closest('.gr-review-text');
                const short = p.querySelector('.gr-text-short');
                const full = p.querySelector('.gr-text-full');
                const isExpanded = btn.dataset.action === 'collapse';

                if (isExpanded) {
                    short.hidden = false;
                    full.hidden = true;
                    btn.textContent = 'Show more';
                    btn.dataset.action = 'expand';
                } else {
                    short.hidden = true;
                    full.hidden = false;
                    btn.textContent = 'Show less';
                    btn.dataset.action = 'collapse';
                }
            });
        });
    }

    // ---- Responsive visible count ----
    _updateVisibleCount() {
        const w = this._container.offsetWidth;
        if (w < 480) {
            this._visibleCount = 1;
        } else if (w < 700) {
            this._visibleCount = 2;
        } else if (w < 960) {
            this._visibleCount = 3;
        } else {
            this._visibleCount = 4;
        }
        // Clamp index
        const maxIndex = this._maxIndex();
        if (this._currentIndex > maxIndex) {
            this._currentIndex = maxIndex;
        }
    }

    _maxIndex() {
        return Math.max(0, this._cards.length - this._visibleCount);
    }

    _setupResizeObserver() {
        this._resizeObserver = new ResizeObserver(() => {
            const prevVisible = this._visibleCount;
            this._updateVisibleCount();
            if (prevVisible !== this._visibleCount) {
                this._buildDots();
                this._updateSlider(false);
            }
        });
        this._resizeObserver.observe(this._container);
    }

    // ---- Dots ----
    _buildDots() {
        const count = this._maxIndex() + 1;
        this._dotsContainer.innerHTML = '';
        this._dots = [];

        if (count <= 1) return;

        for (let i = 0; i < count; i++) {
            const dot = document.createElement('button');
            dot.className = 'gr-dot';
            dot.setAttribute('aria-label', `Go to review ${i + 1}`);
            dot.addEventListener('click', () => {
                this._currentIndex = i;
                this._updateSlider();
                this._resetAutoplay();
            });
            this._dotsContainer.appendChild(dot);
            this._dots.push(dot);
        }
        this._updateDots();
    }

    _updateDots() {
        this._dots.forEach((dot, i) => {
            dot.classList.toggle('is-active', i === this._currentIndex);
        });
    }

    // ---- Slider movement ----
    _cardWidth() {
        if (!this._cards.length) return 0;
        // Gap is 16px (from CSS)
        const containerW = this._container.offsetWidth;
        const gap = 16;
        const totalGap = gap * (this._visibleCount - 1);
        return (containerW - totalGap) / this._visibleCount;
    }

    _translateX() {
        const cw = this._cardWidth();
        const gap = 16;
        return this._currentIndex * (cw + gap);
    }

    _updateSlider(animate = true) {
        if (!animate) {
            this._track.style.transition = 'none';
        } else {
            this._track.style.transition = 'transform 0.45s cubic-bezier(0.25, 0.46, 0.45, 0.94)';
        }

        this._track.style.transform = `translateX(-${this._translateX()}px)`;

        // Update card widths dynamically so they fill correctly
        const cw = this._cardWidth();
        this._cards.forEach(card => {
            card.style.flex = `0 0 ${cw}px`;
            card.style.width = `${cw}px`;
        });

        const maxIndex = this._maxIndex();
        this._prevBtn.disabled = this._currentIndex === 0;
        this._nextBtn.disabled = this._currentIndex >= maxIndex;

        this._updateDots();

        if (!animate) {
            // Force reflow before re-enabling transitions
            this._track.getBoundingClientRect();
        }
    }

    // ---- Navigation ----
    _attachNavListeners() {
        this._prevBtn.addEventListener('click', () => {
            if (this._currentIndex > 0) {
                this._currentIndex--;
                this._updateSlider();
                this._resetAutoplay();
            }
        });

        this._nextBtn.addEventListener('click', () => {
            if (this._currentIndex < this._maxIndex()) {
                this._currentIndex++;
                this._updateSlider();
                this._resetAutoplay();
            }
        });
    }

    // ---- Touch ----
    _attachTouchListeners() {
        this._container.addEventListener('touchstart', e => {
            this._touchStartX = e.changedTouches[0].clientX;
            this._stopAutoplay();
        }, { passive: true });

        this._container.addEventListener('touchend', e => {
            this._touchEndX = e.changedTouches[0].clientX;
            const delta = this._touchStartX - this._touchEndX;

            if (Math.abs(delta) > 40) {
                if (delta > 0 && this._currentIndex < this._maxIndex()) {
                    this._currentIndex++;
                } else if (delta < 0 && this._currentIndex > 0) {
                    this._currentIndex--;
                }
                this._updateSlider();
            }
            this._startAutoplay();
        }, { passive: true });
    }

    // ---- Mouse Drag ----
    _attachDragListeners() {
        this._container.addEventListener('mousedown', e => {
            this._isDragging = true;
            this._dragStartX = e.clientX;
            this._dragOffset = 0;
            this._track.style.cursor = 'grabbing';
            this._stopAutoplay();
            e.preventDefault();
        });

        document.addEventListener('mousemove', e => {
            if (!this._isDragging) return;
            this._dragOffset = this._dragStartX - e.clientX;
            const base = this._translateX();
            this._track.style.transition = 'none';
            this._track.style.transform = `translateX(-${base + this._dragOffset}px)`;
        });

        document.addEventListener('mouseup', () => {
            if (!this._isDragging) return;
            this._isDragging = false;
            this._track.style.cursor = '';

            if (Math.abs(this._dragOffset) > 50) {
                if (this._dragOffset > 0 && this._currentIndex < this._maxIndex()) {
                    this._currentIndex++;
                } else if (this._dragOffset < 0 && this._currentIndex > 0) {
                    this._currentIndex--;
                }
            }
            this._updateSlider();
            this._startAutoplay();
        });
    }

    // ---- Autoplay ----
    _startAutoplay() {
        const enabled = this.dataset.autoplay === 'true';
        if (!enabled) return;
        const speed = parseInt(this.dataset.autoplaySpeed || '4000', 10);
        this._autoplayTimer = setInterval(() => {
            if (this._currentIndex < this._maxIndex()) {
                this._currentIndex++;
            } else {
                this._currentIndex = 0;
            }
            this._updateSlider();
        }, speed);
    }

    _stopAutoplay() {
        if (this._autoplayTimer) {
            clearInterval(this._autoplayTimer);
            this._autoplayTimer = null;
        }
    }

    _resetAutoplay() {
        this._stopAutoplay();
        this._startAutoplay();
    }
}

customElements.define('google-reviews-slider', GoogleReviewsSlider);